import React, { useEffect, useState } from 'react';
import './SubscriptionPage.css';
import { Typography } from '@mui/material';
import { CommonSevice } from '../../services/common';
import { showToast } from '../../utils/common.util';
import { ToastThemes } from '../../constants/common';

const SubscriptionPage = () => {
  const [activePlan, setActivePlan] = useState('standard');
  const [premiumUserCount, setPremiumUserCount] = useState(10);
  const [premiumAdminCount, setPremiumAdminCount] = useState(1);
  const [plans, setPlans] = useState([]);
  const [defaultCount, setDefaultCount] = useState({});
  const [data, setData] = useState({});

  const hostname = window.location.hostname;
  const tenant = hostname.split('.')[0];

  const fetchPlanData = async () => {
    try {
      const apiResponse = await new CommonSevice().fetchPlanDetails();
      setData(apiResponse);
      const activeType = apiResponse.activePlan.type.toLowerCase();
      setActivePlan(activeType);
    } catch (err) {
      showToast('Failed to fetch subscription details.', ToastThemes.error);
    }
  };

  useEffect(() => {
    fetchPlanData();
  }, []);

  useEffect(async () => {
    let parsedPlans = [];
    if (activePlan === 'standard') {
      parsedPlans = [
        {
          name: 'Standard',
          key: 'standard',
          // price: 'Free',
          features: data.activePlan.features.map((feature) => ({
            label: `${feature.name}: ${feature.count} ${
              feature.frequency === 'M'
                ? 'Monthly'
                : feature.frequency === 'Y'
                ? 'Yearly'
                : ''
            }`,
            redirectionLink: feature?.redirectionLink || '',
            subscriptionId: feature?.subscriptionId || '',
          })),
        },
        {
          name: 'Premium',
          key: 'premium',
          // price: `$${
          //   (premiumUserCount * 2) + (premiumAdminCount  * 50)
          // }/month`,
          features: data.secondaryPlan.features.map((feature) => {
            if (feature.name.includes('Users')) {
              setPremiumUserCount(feature.count);
              setDefaultCount((prev) => ({
                ...prev,
                user: {
                  count: feature.count,
                  subscriptionId: feature.subscriptionId,
                },
              }));
            }
            if (feature.name.includes('Admins')) {
              setPremiumAdminCount(feature.count);
              setDefaultCount((prev) => ({
                ...prev,
                admin: {
                  count: feature.count,
                  subscriptionId: feature.subscriptionId,
                },
              }));
            }

            return {
              label: `${feature.name}: ${feature.count} ${
                feature.frequency === 'M'
                  ? 'Monthly'
                  : feature.frequency === 'Y'
                  ? 'Yearly'
                  : ''
              }`,
              controls:
                (feature.name.includes('Users') ||
                  feature.name.includes('Admins')) &&
                activePlan === 'premium'
                  ? true
                  : false,
              redirectionLink: feature?.redirectionLink || '',
              subscriptionId: feature?.subscriptionId || '',
            };
          }),
        },
        {
          name: 'Enterprise',
          key: 'enterprise',
          // price: 'Custom Pricing',
          features: [
            { label: 'Unlimited Users' },
            { label: 'Dedicated Account Manager' },
            { label: 'Custom Integrations & Features' },
            { label: '24/7 Support' },
          ],
        },
      ];
    } else if (activePlan === 'premium') {
      parsedPlans = [
        {
          name: 'Standard',
          key: 'standard',
          // price: 'Free',
          features: data.secondaryPlan.features.map((feature) => ({
            label: `${feature.name}: ${feature.count} ${
              feature.frequency === 'M'
                ? 'Monthly'
                : feature.frequency === 'Y'
                ? 'Yearly'
                : ''
            }`,
            redirectionLink: feature?.redirectionLink || '',
            subscriptionId: feature?.subscriptionId || '',
          })),
        },
        {
          name: 'Premium',
          key: 'premium',
          // price: `$${
          //   (premiumUserCount * 2) + (premiumAdminCount  * 50)
          // }/month`,
          features: data.activePlan.features.map((feature) => {
            if (feature.name.includes('Users')) {
              setPremiumUserCount(feature.count);
              setDefaultCount((prev) => ({
                ...prev,
                user: {
                  count: feature.count,
                  subscriptionId: feature.subscriptionId,
                },
              }));
            }
            if (feature.name.includes('Admins')) {
              setPremiumAdminCount(feature.count);
              setDefaultCount((prev) => ({
                ...prev,
                admin: {
                  count: feature.count,
                  subscriptionId: feature.subscriptionId,
                },
              }));
            }

            return {
              label: `${feature.name}: ${feature.count} ${
                feature.frequency === 'M'
                  ? 'Monthly'
                  : feature.frequency === 'Y'
                  ? 'Yearly'
                  : ''
              }`,
              controls:
                (feature.name.includes('Users') ||
                  feature.name.includes('Admins')) &&
                activePlan === 'premium'
                  ? true
                  : false,
              redirectionLink: feature?.redirectionLink || '',
              subscriptionId: feature?.subscriptionId || '',
            };
          }),
        },
        {
          name: 'Enterprise',
          key: 'enterprise',
          // price: 'Custom Pricing',
          features: [
            { label: 'Unlimited Users' },
            { label: 'Dedicated Account Manager' },
            { label: 'Custom Integrations & Features' },
            { label: '24/7 Support' },
          ],
        },
      ];
    }

    setPlans(parsedPlans);
  }, [activePlan, data]);

  const handleEnterpriseRequest = async () => {
    try {
      // Simulate a backend request
      alert(
        'Enterprise request sent to the backend. We will contact you shortly.'
      );
    } catch (error) {
      alert('Failed to send request. Please try again later.');
    }
  };

  const handlePlanChange = async (planKey) => {
    if (planKey === 'premium') {
      const confirmed = window.confirm('Do you want to activate Premium?');
      if (confirmed) {
        await new CommonSevice()
          .changePlan(
            {
              action: 'ChangePlanFromApp',
              subscriptionId: data.activePlan.subscriptionId,
              planId: data.secondaryPlan.microsoftPlanId,
            },
            tenant
          )
          .then(() => {
            showToast('Thank You! Request Sent', ToastThemes.success);
            fetchPlanData();
          })
          .catch((err) => {
            showToast(
              err?.response?.data?.message || 'Something Went Wrong! Try Again',
              ToastThemes.error
            );
            throw err?.response?.data?.message;
          });
        // setActivePlan(planKey);
      }
    } else {
      const confirmed = window.confirm(
        'Do you want to switch back to Standard?'
      );
      if (confirmed) {
        await new CommonSevice()
          .changePlan(
            {
              action: 'ChangePlanFromApp',
              subscriptionId: data.activePlan.subscriptionId,
              planId: data.secondaryPlan.microsoftPlanId,
            },
            tenant
          )
          .then(() => {
            showToast('Thank You! Request Sent', ToastThemes.success);
            fetchPlanData();
          })
          .catch((err) => {
            showToast(
              err?.response?.data?.message || 'Something Went Wrong! Try Again',
              ToastThemes.error
            );
            throw err?.response?.data?.message;
          });

        // setActivePlan(planKey);
      }
    }
  };

  const handleUpdateCounts = async () => {
    const changes = [];

    if (premiumUserCount !== defaultCount.user.count) {
      changes.push({
        action: 'ChangeQuantityFromApp',
        subscriptionId: defaultCount.user.subscriptionId,
        quantity: premiumUserCount,
      });
    }

    if (premiumAdminCount !== defaultCount.admin.count) {
      changes.push({
        action: 'ChangeQuantityFromApp',
        subscriptionId: defaultCount.admin.subscriptionId,
        quantity: premiumAdminCount,
      });
    }

    if (changes.length === 0) {
      alert('No changes to update.');
      return;
    }

    const confirmed = window.confirm(
      'Are you sure you want to update the quantities?'
    );
    if (!confirmed) return;

    try {
      await new CommonSevice().changeQuantity(changes, tenant);
      showToast('Counts updated successfully!', ToastThemes.success);
      setDefaultCount((prev) => ({
        ...prev,
        user: { ...prev.user, count: premiumUserCount },
        admin: { ...prev.admin, count: premiumAdminCount },
      }));
      fetchPlanData();
    } catch (err) {
      showToast(
        err?.response?.data?.message ||
          'Failed to update counts. Try again later.',
        ToastThemes.error
      );
      throw err?.response?.data?.message;
    }
  };

  const incrementUserCount = (redirectionLink, subscriptionId) => {
    setPremiumUserCount((prevCount) => prevCount + 1);
  };

  const decrementAdminCount = (redirectionLink, subscriptionId) => {
    if (premiumAdminCount <= 1) {
      showToast(
        'Minimum admin count is 1. You cannot reduce it further.',
        ToastThemes.error
      );
      return;
    }
    setPremiumAdminCount(premiumAdminCount - 1);
  };

  const incrementAdminCount = (redirectionLink, subscriptionId) => {
    if (!subscriptionId) {
      const confirmed = window.confirm(
        'It looks like your organization does not have an active Admin subscription plan. To purchase additional Admin seats, please proceed to the subscription page.\n\nWould you like to continue to the purchase page?'
      );
      if (confirmed && redirectionLink) {
        window.open(redirectionLink, '_blank');
      }
      return;
    }
    setPremiumAdminCount((prevCount) => prevCount + 1);
  };

  const decrementUserCount = (redirectionLink, subscriptionId) => {
    if (premiumUserCount <= 10) {
      showToast(
        'Minimum user count is 10. You cannot reduce it further.',
        ToastThemes.error
      );
      return;
    }
    setPremiumUserCount(premiumUserCount - 1);
  };

  return (
    <div className="subscription-page">
      <h1>Choose Your Subscription Plan</h1>
      <p className="info">
        Select a plan that best fits your needs. You can always switch between
        plans.
      </p>
      <br />
      <div className="plans-container">
        {plans.map((plan) => (
          <div
            key={plan.key}
            className={`plan-card ${activePlan === plan.key ? 'active' : ''}`}
          >
            <h2
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {plan.name}
            </h2>
            <p className="price">{plan.price}</p>
            <ul>
              {plan.features.map((feature, index) => (
                <li key={index} className="feature-item">
                  <Typography
                    marginBottom={1}
                    fontWeight={
                      feature.label.includes('Users') ||
                      feature.label.includes('Admins')
                        ? 'bolder'
                        : ''
                    }
                  >
                    {feature.label}
                  </Typography>
                  {feature.controls && (
                    <div className="feature-controls">
                      {feature.label.includes('Users') && (
                        <>
                          <button
                            onClick={() =>
                              decrementUserCount(
                                feature.redirectionLink,
                                feature.subscriptionId
                              )
                            }
                          >
                            -
                          </button>
                          <span>
                            {premiumUserCount < 10 ? 0 : ''}
                            {premiumUserCount}
                          </span>
                          {/* <input
                            type="number"
                            min={10}
                            value={premiumUserCount}
                            onChange={(e) => {
                              const val = parseInt(e.target.value, 10);
                              if (!isNaN(val)) {
                                if (val < 10) {
                                  showToast(
                                    'Minimum user count is 10. You cannot reduce it further.',
                                    ToastThemes.error
                                  );
                                  return;
                                }
                                setPremiumUserCount(val);
                              }
                            }}
                            className="quantity-input"
                          /> */}
                          <button
                            onClick={() =>
                              incrementUserCount(
                                feature.redirectionLink,
                                feature.subscriptionId
                              )
                            }
                          >
                            +
                          </button>
                        </>
                      )}
                      {feature.label.includes('Admins') && (
                        <>
                          <button
                            onClick={() =>
                              decrementAdminCount(
                                feature.redirectionLink,
                                feature.subscriptionId
                              )
                            }
                          >
                            -
                          </button>
                          <span>
                            {premiumAdminCount < 10 ? 0 : ''}
                            {premiumAdminCount}
                          </span>
                          {/* <input
                            type="number"
                            min={1}
                            value={premiumAdminCount}
                            onChange={(e) => {
                              const val = parseInt(e.target.value, 10);
                              if (!isNaN(val)) {
                                if (val < 1) {
                                  showToast(
                                    'Minimum admin count is 1. You cannot reduce it further.',
                                    ToastThemes.warning
                                  );
                                  return;
                                }
                                setPremiumAdminCount(val);
                              }
                            }}
                            className="quantity-input"
                          /> */}
                          <button
                            onClick={() =>
                              incrementAdminCount(
                                feature.redirectionLink,
                                feature.subscriptionId
                              )
                            }
                          >
                            +
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <div className="plan-actions">
              {plan.key === 'premium' && activePlan === 'premium' && (
                <button onClick={handleUpdateCounts}>Update Counts</button>
              )}

              {plan.key === 'premium' && activePlan !== 'premium' && (
                <button onClick={() => handlePlanChange(plan.key)}>
                  Upgrade to Premium
                </button>
              )}

              {plan.key === 'standard' && activePlan !== 'standard' && (
                <button onClick={() => handlePlanChange(plan.key)}>
                  Switch to Standard
                </button>
              )}

              {plan.key === 'enterprise' && (
                <button onClick={handleEnterpriseRequest} disabled>
                  Request Enterprise Plan
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SubscriptionPage;

// .quantity-input {
//   width: 50px;
//   text-align: center;
//   font-weight: bold;
//   padding: 4px;
//   border-radius: 4px;
//   border: 1px solid #ccc;
// }